import { Component } from '@angular/core';
import { BaseBPProfilesDetailComponent } from '../base-bp-profiles-detail.component';
import { BPProfilesService, BPProfilesViewCode } from '../../bp-profiles.service';
import { FormlyInputType, FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { BPProfilesContractDataService } from './bp-profiles-contract-data.service';
import { LOV_ACTIVE_ACSL_USERS, LOV_CURRENCY, LovService } from '../../../../shared/services/lov.service';
import * as _ from 'underscore';
import { BPProfilesDataService } from '../../bp-profiles-data.service';
import { ContractAgreedComponent } from './contract-agreed.component';
import { IBPProfile } from '../../../../../type_definitions/datatypes';
import { util } from '../../../../shared/util';
import { FaqPopupComponent } from '../../../../shared/faq/faq-popup.component';
import { FAQ_BP_AGREEMENT } from '../../../../shared/faq/faq-codes';
import { GaValidateService } from '../../../../shared/formly/ga-validate.service';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { Override } from '../../../../shared/app-decorators';

@Component({
  selector: 'bp-profiles-contract',
  template: `
    <h2 *ngIf="loading"><activity-indicator></activity-indicator></h2>
    <formly [form]="form" [fields]="fields" [model]="model" *ngIf="!loading"></formly>
  `
})
export class BPProfilesContractComponent extends BaseBPProfilesDetailComponent {
  public model:IBPProfile;

  private memberFeeCurrencyField:IExtraFieldConfigurationObject;
  private gbdmField:IExtraFieldConfigurationObject;
  private urgencyField:IExtraFieldConfigurationObject;
  private hasWritePermission:boolean;

  constructor(
    private validateService:GaValidateService,
    private bpProfilesContractDataService:BPProfilesContractDataService,
    private lovService:LovService,
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private formlyService:GaFormlyService,
    private bpProfilesDataService:BPProfilesDataService,
    private authService:AuthService,
    protected bpProfilesService:BPProfilesService
  ) {
    super(bpProfilesService);
  }

  @Override(BaseBPProfilesDetailComponent)
  public ngOnInit():void {
    this.hasWritePermission = this.authService.viewSpecial(BPProfilesViewCode.CONTRACT);
    super.ngOnInit();
  }

  private get isEditable():boolean {
    return this.bpProfilesService.editingMode && this.hasWritePermission;
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    const fullWidthClass:string = 'col-sm-12';
    const halfWidthClass:string = 'col-sm-6';
    const isEditingMode:boolean = this.bpProfilesService.editingMode;
    const fullWidthInEdit:string = this.isEditable ? fullWidthClass : undefined;

    return [
      this.formlyService.aTitle('BPPROFILES.CONTRACT.AGREEMENT.TITLE')
        .build(),
      this.formlyService.aGroup()
        .withoutWrapper()
        .withWrapperClass(fullWidthClass)
        .withFields([
          this.formlyService.aTemplate(`${this.translationService.instant('BPPROFILES.CONTRACT.AGREEMENT_INFO')}`)
            .build(),
          this.formlyService.aCustom()
            .withComponent(FaqPopupComponent)
            .withoutWrapper()
            .withExtraOptions({
              templateOptions: {
                inputs: {
                  faqCode: FAQ_BP_AGREEMENT,
                  label: 'BPPROFILES.CONTRACT.SHOW_AGREEMENT'
                }
              }
            })
            .build(),
          this.formlyService.aTemplate(`<p class="margin-top-sm">${this.translationService.instant('BPPROFILES.CONTRACT.AGREEMENT_INFO_EXTRA')}</p>`)
            .build(),
          this.formlyService.aField(isEditingMode? FormlyType.CHECKBOX : FormlyType.VALUE, 'contractAgreed')
            .withFormat(this.formattingService.formatBoolean)
            .build(),
          this.formlyService.aField(isEditingMode? FormlyType.TEXTAREA : FormlyType.VALUE, 'contractBPComment')
            .withLabelClass(fullWidthClass)
            .withValueClass(fullWidthClass)
            .build(),
          this.formlyService.aCustom()
            .withoutWrapper()
            .withComponent(ContractAgreedComponent)
            .withExtraOptions({
              templateOptions: {
                inputs: {
                  contact: this.model.contractAgreedBy,
                  agreementDate: this.model.contractAgreedOn
                }
              }
            })
            .withHideExpression(() => isEditingMode || !this.model.contractAgreed)
            .build()
        ])
        .build(),
      this.formlyService.aTitle('BPPROFILES.CONTRACT.MEMBER_FEE.TITLE')
        .build(),
      this.formlyService.aGroup()
        .withoutWrapper()
        .withFields([
          this.formlyService.aField(this.isEditable? FormlyType.INPUT : FormlyType.VALUE, 'memberFixedOFFee')
            .withWrapperClass(halfWidthClass)
            .withLabelClass(fullWidthInEdit)
            .withValueClass(fullWidthInEdit)
            .withInputType(FormlyInputType.NUMBER)
            .withExtraOptions({
              templateOptions: {
                min: 0
              }
            })
            .build(),
          this.formlyService.aField(this.isEditable? FormlyType.INPUT : FormlyType.VALUE, 'memberPercentageOFFee')
            .withWrapperClass(halfWidthClass)
            .withLabelClass(fullWidthInEdit)
            .withValueClass(fullWidthInEdit)
            .withInputType(FormlyInputType.NUMBER)
            .withExtraOptions({
              templateOptions: {
                min: 0,
                max: 100
              }
            })
            .build()
        ])
        .build(),
      this.formlyService.aGroup()
        .withoutWrapper()
        .withFields([
          this.memberFeeCurrencyField = this.formlyService.aField(this.isEditable? FormlyType.SELECT : FormlyType.VALUE, 'memberFeeCurrency')
            .withFormat(this.formattingService.formatCurrencyS)
            .withWrapperClass(halfWidthClass)
            .withLabelClass(fullWidthInEdit)
            .withValueClass(fullWidthInEdit)
            .build(),
          this.formlyService.aValue('memberFeeFrequency')
            .withWrapperClass(halfWidthClass)
            .withHideExpression(() => this.isEditable)
            .withFormat((frequency:string) => _.findWhere(this.bpProfilesContractDataService.feeFrequencies, { id: frequency })?.value)
            .build(),
          this.formlyService.aSelect('memberFeeFrequency')
            .withWrapperClass(halfWidthClass)
            .withLabelClass(fullWidthClass)
            .withValueClass(fullWidthClass)
            .withLabelProp('value')
            .withValueProp('id')
            .withOptions(this.bpProfilesContractDataService.feeFrequencies)
            .withHideExpression(() => !this.isEditable)
            .build(),
          this.formlyService.aField(this.isEditable? FormlyType.TEXTAREA : FormlyType.VALUE, 'memberFeeComment')
            .withWrapperClass(fullWidthClass)
            .withLabelClass(fullWidthClass)
            .withValueClass(fullWidthClass)
            .build()
        ])
        .build(),
      this.formlyService.aTitle('BPPROFILES.CONTRACT.TITLE')
        .build(),
      this.formlyService.aGroup()
        .withoutWrapper()
        .withFields([
          this.formlyService.aValue('contractDate', 'GA.GENERAL.CREATION_DATE')
            .withoutEditable()
            .withWrapperClass(fullWidthClass)
            .withLabelClass(fullWidthClass)
            .withValueClass(fullWidthClass)
            .withFormat(this.formattingService.formatDateMedium)
            .build(),
          this.formlyService.aField(this.isEditable? FormlyType.SEARCH : FormlyType.VALUE, 'requestedByCompany')
            .withFormat(this.formattingService.formatCompany)
            .withWrapperClass(halfWidthClass)
            .withLabelClass(fullWidthInEdit)
            .withValueClass(fullWidthInEdit)
            .withUrl(this.isEditable? this.bpProfilesDataService.bpCompaniesSearchUrl : undefined)
            .build(),
          this.urgencyField = this.formlyService.aField(this.isEditable? FormlyType.SELECT : FormlyType.VALUE, 'urgency')
            .withFormat(this.formattingService.formatLov)
            .withWrapperClass(halfWidthClass)
            .withLabelClass(fullWidthInEdit)
            .withValueClass(fullWidthInEdit)
            .build(),
          this.gbdmField = this.formlyService.aField(this.isEditable? FormlyType.SELECT2 : FormlyType.VALUE, 'gBDM')
            .withFormat(this.formattingService.formatContact)
            .withWrapperClass(halfWidthClass)
            .withLabelClass(fullWidthInEdit)
            .withValueClass(fullWidthInEdit)
            .build(),
          this.formlyService.aField(this.isEditable? FormlyType.TEXTAREA : FormlyType.VALUE, 'contractStatusComment', 'GA.GENERAL.COMMENTS')
            .withoutEditable()
            .withWrapperClass(fullWidthClass)
            .withLabelClass(fullWidthClass)
            .withValueClass(fullWidthClass)
            .build()
        ])
        .build()
    ];
  }

  protected fillFields():void {
    this.formlyService.fillField(this.memberFeeCurrencyField, this.lovService.list(LOV_CURRENCY));
    this.formlyService.fillField(this.gbdmField, this.lovService.list(LOV_ACTIVE_ACSL_USERS));
    this.formlyService.fillField(this.urgencyField, this.lovService.list('DOURGEN'));
  }

  protected onSave = ():void => {
    const data:IBPProfile = util.copyAndExtend(this.bpProfilesService.profileDetail, this.model);

    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        this.setSaveBusy(true);
        this.bpProfilesDataService.updateBPProfileDetail(this.bpProfilesService.activeProfile?.id, data, this.translationService.instant('BPPROFILES.CONTRACT.TITLE'))
          .then((profileDetail:IBPProfile) => {
            this.bpProfilesService.profileDetail = profileDetail;
            this.onProfileDetailLoaded(profileDetail);
          })
          .catch(util.noop)
          .finally(() => {
            this.setSaveBusy(false);
          });
      })
      .catch(util.noop);
  };
}